import React, { useState, useEffect, useContext, useCallback } from "react";
import { useParams } from "react-router-dom";
import { DataContext } from "../../../context/DataContext";

import { getRol, getToken, getUser } from "../../../api/Account";
import { getSaldoCarteraAsync } from "../../../api/Report";
import { getGestoresAsync } from "../../../api/Users";
import { simpleMessage } from "../../../helpers/Helpers";
import { formatearFecha, formatearMonto } from "../../../helpers/formato";
import ReporteAgrupado from "../../../components/reportes/ReporteAgrupado";

const TODOS = "t";
// Enlaces anteriores: el tercer segmento de la ruta era 0 = por nombre, 1 = por estado (Tipo).
const ORDEN_POR_ESTADO = 1;

// Un crédito por fila: sus montos se suman directo.
const dinero = (key, label) => ({
  key,
  label,
  align: "center",
  total: true,
  formatoTotal: formatearMonto,
  valor: (c) => formatearMonto(c[key]),
  excel: (c) => c[key],
});

const fecha = (key, label) => ({
  key,
  label,
  align: "center",
  valor: (c) => formatearFecha(c[key]),
  excel: (c) => formatearFecha(c[key]),
});

// "Atraso" es el monto atrasado (no días). Tipo: D = al día, M = en atraso, V = vencido.
const COLUMNAS = [
  { key: "id", label: "#", align: "center", valor: (c) => c.id, excel: (c) => c.id },
  fecha("fechaEntrega", "F.Entrega"),
  { key: "fullName", label: "Nombre Completo", align: "left", texto: true, valor: (c) => c.fullName },
  dinero("montoEntregado", "Monto Entregado"),
  { key: "tipo", label: "Tipo", align: "center", texto: true, valor: (c) => c.tipo },
  fecha("fechaVencimiento", "F.Vencimiento"),
  dinero("atraso", "Atraso"),
  dinero("saldoCapital", "Saldo Capital"),
  dinero("saldoIC", "Saldo I.C."),
  dinero("saldoTotal", "Saldo Total"),
];

// Con saldo 0 no hay porcentaje que dar.
const porcentaje = (parte, saldo) =>
  saldo > 0 ? `${((parte / saldo) * 100).toFixed(2)} %` : "—";

// Total Mora es el atraso de todos los créditos y Total Vencido el saldo total de los de
// tipo V, ambos sobre el saldo pendiente (capital + I.C. de las cuotas no canceladas), como
// el consolidado de antes. saldoTotal del endpoint también suma cuotas canceladas.
const resumen = (filas) => {
  const suma = (lista, campo) => lista.reduce((total, c) => total + c[campo], 0);
  const saldo = suma(filas, "saldoCapital") + suma(filas, "saldoIC");
  const atraso = suma(filas, "atraso");
  const vencido = suma(
    filas.filter((c) => c.tipo === "V"),
    "saldoTotal"
  );

  return [
    {
      label: "Total Mora",
      valor: `${formatearMonto(atraso)} = ${porcentaje(atraso, saldo)}`,
    },
    {
      label: "Total Vencido",
      valor: `${formatearMonto(vencido)} = ${porcentaje(vencido, saldo)}`,
    },
  ];
};

const SaldosCartera = () => {
  const [data, setData] = useState([]);
  const [gestorName, setGestorName] = useState("Todos");

  const { selectedGestor, selectedSortBy } = useParams();
  const { setIsLoading, setIsUnautorized, title, slogan, icon } =
    useContext(DataContext);

  const token = getToken();
  const user = getUser();
  const rol = getRol();

  const manejarError = useCallback(
    (result) => {
      if (result.error === "Unauthorized") {
        setIsUnautorized(true);
        return;
      }
      simpleMessage(result.error, "error");
    },
    [setIsUnautorized]
  );

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      const [reporte, gestores] = await Promise.all([
        getSaldoCarteraAsync(token, {
          selectedSortBy,
          gestorId: selectedGestor === TODOS ? 0 : selectedGestor,
        }),
        selectedGestor === TODOS ? null : getGestoresAsync(token),
      ]);
      setIsLoading(false);

      if (!reporte.statusResponse) {
        manejarError(reporte);
        return;
      }
      setData(reporte.data.result);

      if (selectedGestor !== TODOS) {
        // Si la lista de gestores falla o no lo trae (dado de baja), el nombre sale del reporte.
        const gestor =
          gestores && gestores.statusResponse
            ? gestores.data.result.find((g) => g.id.toString() === selectedGestor)
            : null;
        setGestorName(
          gestor ? gestor.fullName : reporte.data.result[0]?.gestorNombre ?? selectedGestor
        );
      }
    })();
  }, [token, selectedGestor, selectedSortBy, setIsLoading, manejarError]);

  // El servidor manda el nombre del gestor del crédito: también el de uno dado de baja.
  const agruparPor = useCallback((fila) => fila.gestorNombre, []);

  return (
    <ReporteAgrupado
      tituloReporte="Reporte de Saldos Cartera"
      nombreArchivo="SaldosCartera"
      parametros={[{ label: "Gestor Seleccionado", valor: gestorName }]}
      columnas={COLUMNAS}
      data={data}
      agruparPor={agruparPor}
      resumen={resumen}
      ordenInicial={
        parseInt(selectedSortBy) === ORDEN_POR_ESTADO ? "tipo" : "fullName"
      }
      contexto={{ title, slogan, icon, user, rol }}
    />
  );
};

export default SaldosCartera;
